import React, { Suspense, useState, useEffect } from 'react'

import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import ErrorBanner from './ErrorBanner'
import { useErrorContext } from '../ErrorContext'
import { Button, Card, Flex, Grid, Heading, Loader, Menu, MenuButton, MenuItem, Text } from '@aws-amplify/ui-react'
import { extractReferralCodeFromUrl, storeReferralCodeForProcessing } from '../utils/referralUtils'
import { logger } from '../logging'
import { MobileNav } from './MobileNav'
import { DesktopNav } from './DesktopNav'
import { useAuthenticator, fetchUserAttributes, getCurrentUser } from '../amplify'
import { ComponentSizeProvider, useMediaQuery } from './ResponsiveSize'


export function HelpMenu() {
    const navigate = useNavigate()

    return (
        <Menu
            trigger={
                <MenuButton variation='menu'>
                    Help
                </MenuButton>
            }
        >
            <MenuItem onClick={() => navigate('/doc')}>Documentation</MenuItem>
            <MenuItem onClick={() => navigate('/faq')}>FAQ</MenuItem>
            <MenuItem onClick={() => navigate('/issue')}>Issue</MenuItem>
            <MenuItem onClick={() => navigate('/support')}>Support</MenuItem>

        </Menu>
    )
}

// MobileNav is re-exported from its own module for backward compatibility
export { MobileNav } from './MobileNav'

export function Layout() {
    const { authStatus, signOut, user, username } = useAuthenticator((context) => [
        context.authStatus,
        context.signOut,
        context.user,
        context.username
    ])

    const [userDisplayName, setUserDisplayName] = useState<string>('Account')
    const navigate = useNavigate()
    const { error, setError } = useErrorContext()
    useAuthenticator((context: any) => [context.route])
    const location = useLocation()
    const isMobile = useMediaQuery('(max-width: 768px)')

    // Check for referrer code in URL parameters on component mount
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search)
        const urlReferrerCode = extractReferralCodeFromUrl(urlParams)
        if (urlReferrerCode) {
            storeReferralCodeForProcessing(urlReferrerCode)
        }
    }, [location.search])

    // Fetch user attributes to get email for federated users
    useEffect(() => {
        const getUserDisplayName = async () => {
            if (authStatus === 'authenticated') {
                try {
                    let currentUser = user

                    // If user is undefined (common with auto-confirmed users), fetch it directly
                    if (!currentUser) {
                        logger.debug('User is undefined, fetching current user...')
                        currentUser = await getCurrentUser()
                        logger.debug('Fetched current user:', JSON.stringify(currentUser, null, 2))
                    }

                    // Fetch user attributes (Amplify v6 requires explicit fetch)
                    const userAttributes = await fetchUserAttributes()
                    logger.debug('User attributes:', JSON.stringify(userAttributes, null, 2))

                    if (userAttributes.email) {
                        setUserDisplayName(userAttributes.email)
                        return
                    }
                    if (userAttributes.phone_number) {
                        setUserDisplayName(userAttributes.phone_number)
                        return
                    }

                    // Try direct access to user properties (Amplify v6 structure)
                    if (currentUser?.signInDetails?.loginId) {
                        setUserDisplayName(currentUser.signInDetails.loginId)
                        return
                    }

                    // Fallback to username
                    setUserDisplayName(currentUser?.username || 'Account')
                } catch (error) {
                    logger.error('Error fetching user attributes:', error)
                    setUserDisplayName(user?.username || 'Account')
                }
            }
        }

        getUserDisplayName()
    }, [authStatus, user])

    function logOut() {
        signOut()
        navigate('/login')
    }

    // Don't block the entire layout while auth is configuring — let child
    // components handle their own loading states with skeleton placeholders.
    logger.info(`user: ${JSON.stringify(user)}, ${JSON.stringify(username)}`)

    return (
        <ComponentSizeProvider>
            <div style={{ width: '100%', textAlign: 'center' }}>
                <Grid templateRows="0fr 0fr 1fr 0fr"
                    alignContent={"center"}
                    alignItems={"stretch"}
                    width="100%"
                >
                    {isMobile ? (
                        <MobileNav
                            authStatus={authStatus}
                            userDisplayName={userDisplayName}
                            onSignOut={logOut}
                        />
                    ) : (
                        <DesktopNav
                            authStatus={authStatus}
                            userDisplayName={userDisplayName}
                            onSignOut={logOut}
                        />
                    )}
                    <Card>
                        {error && <ErrorBanner error={error} setError={setError} />}
                    </Card>
                    <div className="page-container">
                        <Suspense fallback={
                            <Flex direction="column" alignItems="center" justifyContent="center" padding="4rem">
                                <Loader size="large" />
                                <Text marginTop="1rem">Loading...</Text>
                            </Flex>
                        }>
                            <Outlet />
                        </Suspense>
                    </div>
                    <Card>
                        <Flex direction={"row"}
                            justifyContent={"center"}
                            wrap="wrap"
                        >
                            <Card>
                                <Flex direction={"column"}>
                                    <Heading level={3}>About</Heading>
                                    <Button variation='menu' size='small' onClick={() => navigate('/contactus')}>Contact Us</Button>
                                </Flex>
                            </Card>
                            <Card>
                                <Flex direction={"column"}>
                                    <Heading level={3}>Legal</Heading>
                                    <Button variation='menu' size='small' onClick={() => navigate('/privacy')}>Privacy Policy</Button>
                                    <Button variation='menu' size='small' onClick={() => navigate('/terms')}>Terms Of Use</Button>
                                </Flex>
                            </Card>
                        </Flex>
                    </Card>
                </Grid>
            </div>
        </ComponentSizeProvider>
    )
}
